import { zodResolver } from "@hookform/resolvers/zod";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  TextareaAutosize,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { sentenceCase } from "change-case";
import { proposalContentTypeName } from "constants/proposalSettingsDisplayType";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import {
  getDefaultValues as getSendProposalDefaultValues,
  mapContentSettingsInput,
  sendProposalSchema,
  SendProposalFormValues,
  toProposalMutationVariables,
} from "DDD/action-objects/ProposalSend";
import {
  PreviewProposalMutationVariables,
  ProposalLayout,
  SendProposalMutationVariables,
} from "generated/graphql";
import useSendProposal from "hooks/proposals/useSendProposal";
import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import { getRoute } from "utils/routing";
import ProposalContext from "./providers/ProposalContextProvider";
import usePreviewProposal from "hooks/proposals/usePreviewProposal";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useGetOrganization from "hooks/organization/useGetOrganization";
import CustomMultipleSelect from "components/Shared/CustomMultipleSelect/CustomMultipleSelect";
import { getOrganizationDocumentName } from "modules/organization/UpdateOrganization/DocumentItem";
import useGetOrganizationProposalContent from "hooks/organization/useGetorganizationProposalContent";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import { ProposalFormsContext } from "./providers/ProposalFormsContextProvider";
import { DefinedButtons } from "components/Sidebar";
import { SidebarContext } from "components/Sidebar/sidebarContext";
import Modal from "modules/Modal/Modal";
import ProposalStageSummary from "./ProposalStageSummary";
import ProposalConfirmationModal from "./ProposalConfirmationModal";
import { getMissingJobSizeMessage } from "./validators";

export default function SendProposal() {
  const { data } = useGetOrganization();
  const { proposalContent, loading: getContentLoading } = useGetOrganizationProposalContent();
  const { proposal } = useContext(ProposalContext);
  const navigate = useNavigate();
  const [sendProposal, { isSuccess }] = useSendProposal({ id: proposal.id });
  const [previewProposal, { isSuccess: previewSuccess }] = usePreviewProposal();
  const [error, setError] = useState<null | string>(null);
  type AcceptFnProps = "send" | "preview";
  const [acceptFnType, setAcceptFnType] = useState<AcceptFnProps>("preview");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [modalActionType, setModalActionType] = useState<"send" | "preview">("preview");
  const [emails, setEmails] = useState<string[]>([]);
  const [lastPreviewVariables, setLastPreviewVariables] =
    useState<PreviewProposalMutationVariables | null>(null);

  const defaultValues = useMemo(() => getSendProposalDefaultValues(proposal as any), [proposal]);
  const methods = useForm<SendProposalFormValues>({
    resolver: zodResolver(sendProposalSchema),
    defaultValues,
    values: defaultValues,
  });

  const proposalSectionOptions = useMemo(
    () =>
      (proposal.contentSettings ?? [])
        .filter((item) => item.contentType?.toLowerCase() !== "invoice_details")
        .map((item) => ({
          label:
            proposalContentTypeName[item.contentType.toUpperCase()] ??
            sentenceCase(item.contentType),
          value: item.id,
        })),
    [proposal.contentSettings]
  );

  const handleProposalSectionsChange = useCallback(
    (selectedIds: string[]) => {
      methods.setValue("contentSettingIds", selectedIds);
    },
    [methods]
  );

  const isMobile = useMediaQuery(
    "(max-width: 428px), screen and (min-width: 428px) and (max-width: 926px) and (orientation: landscape)"
  );

  const unifiedPreviewHandler = useCallback(
    async (emailData?: { subject?: string; body?: string; emails?: string[] }) => {
      const variables = {
        proposalId: proposal.id,
        layout: methods.getValues("layout") as ProposalLayout,
        removeUnitPriceAndQuantity: methods.getValues("removeUnitPriceAndQuantity"),
        removeIndividualLineItemPricing: methods.getValues("removeIndividualLineItemPricing"),
        attachments: methods.getValues("attachments"),
        acceptanceOverride: methods.getValues("acceptanceOverride"),
        emailSubjectOverride: emailData?.subject || methods.getValues("emailSubjectOverride"),
        emailBodyOverride: emailData?.body || methods.getValues("emailBodyOverride"),
        clientEmailOverride: emailData?.emails?.[0] || methods.getValues("clientEmailOverride"),
        contentSettings: mapContentSettingsInput(
          proposal.contentSettings,
          methods.getValues("contentSettingIds")
        ),
      };

      setLastPreviewVariables(variables);

      const missingJobSizeMessage = getMissingJobSizeMessage(proposal);
      if (missingJobSizeMessage) {
        setAcceptFnType("preview");
        setError(missingJobSizeMessage);
        setOpen(true);
        return;
      }

      if (isMobile) {
        // Mobile: Open frontend preview URL directly in new tab
        const previewUrl = getRoute("proposals.preview", [["proposalId", proposal.id]]);
        const urlParams = new URLSearchParams({
          ...variables,
          layout: variables.layout as string,
          removeUnitPriceAndQuantity: String(variables.removeUnitPriceAndQuantity ?? false),
          removeIndividualLineItemPricing: String(
            variables.removeIndividualLineItemPricing ?? false
          ),
          attachments: JSON.stringify(variables.attachments || []),
          contentSettings: JSON.stringify(variables.contentSettings || null),
        });
        window.open(`${previewUrl}?${urlParams.toString()}`, "_blank");
      } else {
        // Desktop: Call backend and open result URL in new tab
        const result = await previewProposal({
          variables,
        });
        if (previewSuccess(result.data) && result.data?.previewProposal) {
          window.open(result.data.previewProposal, "_blank");
        } else {
          handleJobSizeError(result);
        }
      }
    },
    [proposal.id, proposal.contentSettings, methods, isMobile, previewProposal, previewSuccess]
  );

  const { productsFormsStatus } = useContext(ProposalFormsContext);
  const productsWithPendingChanges = Object.keys(productsFormsStatus).filter(
    (productId) => productsFormsStatus[productId].isDirty
  );
  const proposalIsDirty = productsWithPendingChanges?.length > 0;
  const handleSendProposal = useCallback(
    async (variables: SendProposalMutationVariables) => {
      setAcceptFnType("send");
      const missingJobSizeMessage = getMissingJobSizeMessage(proposal);
      if (missingJobSizeMessage && !variables.forceSend) {
        setError(missingJobSizeMessage);
        setOpen(true);
        return false;
      }
      const result = await sendProposal({
        variables,
      });

      if (isSuccess(result.data)) {
        navigate(getRoute("proposals.info", [["proposalId", proposal.id]]));
        return true;
      }
      handleJobSizeError(result);
    },
    [sendProposal]
  );

  // https://app.clickup.com/t/8693kbaep
  const handleJobSizeError = (result) => {
    if (
      // @ts-ignore
      result?.name === "ApolloError" &&
      // @ts-ignore
      result?.message?.indexOf("Please input a job size.") !== -1
    ) {
      // @ts-ignore
      setError(result.message);
      setOpen(true);
    }
  };

  const allDocuments = useMemo(() => {
    if (!data || !proposal) return [];
    return [...data?.documents, ...proposal?.attachments];
  }, [data, proposal]);

  const getDocumentLabel = useCallback(
    (id) => {
      if (!allDocuments) return "";
      const document = allDocuments?.find((d) => d.id === id);
      return getOrganizationDocumentName(document);
    },
    [data]
  );

  const { clickedButton, setButtonDisabled } = useContext(SidebarContext);

  useEffect(() => {
    if (clickedButton == DefinedButtons.ProposalPreview) {
      unifiedPreviewHandler();
    }
    if (clickedButton == DefinedButtons.ProposalSummaryView) {
      setOpenModal(true);
    }
  }, [clickedButton, unifiedPreviewHandler]);

  type ActionMap = {
    [K in AcceptFnProps]: {
      method: (
        variables: SendProposalMutationVariables | PreviewProposalMutationVariables
      ) => Promise<boolean>;
      variables: SendProposalMutationVariables | PreviewProposalMutationVariables;
    };
  };

  const acceptFn = useCallback(async () => {
    const currentValues = methods.getValues() as SendProposalFormValues;
    const sendValues = toProposalMutationVariables(
      currentValues,
      proposal.contentSettings
    ) as SendProposalMutationVariables;
    const actionMap: ActionMap = {
      send: {
        method: handleSendProposal,
        variables: { ...sendValues, emails, forceSend: true },
      },
      preview: {
        method: async () => {
          if (!lastPreviewVariables) return true;

          const result = await previewProposal({
            variables: { ...lastPreviewVariables, forceSend: true },
          });

          if (previewSuccess(result.data) && result.data?.previewProposal) {
            window.open(result.data.previewProposal, "_blank");
          }
          return true;
        },
        variables: {
          // Add a dummy structure that satisfies the type checker
          // This won't be used since the method is custom
          proposalId: proposal.id,
          layout: ProposalLayout.SIMPLE,
        },
      },
    };

    const { method, variables } = actionMap[acceptFnType];

    return method(variables);
  }, [
    handleSendProposal,
    acceptFnType,
    emails,
    unifiedPreviewHandler,
    lastPreviewVariables,
    previewProposal,
    previewSuccess,
    methods,
    proposal.contentSettings,
  ]);

  const cancelFn = useCallback(async () => {
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const onClose = () => {
    setOpenModal(false);
  };

  const openSendConfirmModal = () => {
    setModalActionType("send");
    setOpenConfirmModal(true);
  };

  const openPreviewConfirmModal = () => {
    setModalActionType("preview");
    setOpenConfirmModal(true);
  };

  const hasValidStages = useMemo(() => proposal?.stages?.[0]?.name, [proposal]);

  const stageValidationMessage = useMemo(() => {
    if (!hasValidStages) {
      return "Please add a name to the first stage before sending or previewing the proposal.";
    }
    return "";
  }, [hasValidStages]);

  useEffect(() => {
    setButtonDisabled(DefinedButtons.ProposalPreview, {
      disabled: proposalIsDirty || !hasValidStages,
      tooltip:
        stageValidationMessage ||
        (proposalIsDirty &&
          "Please save your changes and review for accuracy to preview the proposal"),
    });
  }, [proposalIsDirty, hasValidStages, stageValidationMessage, setButtonDisabled]);

  return (
    <>
      <MDBox
        component="form"
        role="form"
        onSubmit={(e) => {
          e.preventDefault();
          openSendConfirmModal();
        }}
      >
        <Accordion>
          <AccordionSummary
            sx={{
              backgroundColor: "rgba(100, 100, 150, 0.2)",
              marginTop: "30px",
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <MDTypography>Proposal Options</MDTypography>
          </AccordionSummary>
          <AccordionDetails>
            <MDBox
              p={3}
              component="form"
              role="form"
              onSubmit={methods.handleSubmit((values) => {
                return handleSendProposal(
                  toProposalMutationVariables(
                    values as SendProposalFormValues,
                    proposal.contentSettings
                  ) as SendProposalMutationVariables
                );
              })}
            >
              <MDBox pb={6}>
                <FormProvider {...methods}>
                  <MDBox>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Controller
                              name="removeUnitPriceAndQuantity"
                              control={methods.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                              )}
                            />
                          }
                          label="Remove Unit Price And Quantity"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Controller
                              name="removeIndividualLineItemPricing"
                              control={methods.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                              )}
                            />
                          }
                          label="Remove Individual Line Item Pricing"
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                  <Recipients />
                  <Grid container spacing={3} mt={1} mb={4}>
                    <Grid item xs={12} md={6}>
                      {data?.documents && (
                        <Controller
                          name="attachments"
                          control={methods.control}
                          render={({ field }) => (
                            <CustomMultipleSelect
                              {...field}
                              fullWidth
                              onChange={(e) => {
                                field.onChange(e?.map((x) => x.value as string));
                              }}
                              label="Proposal Attachments"
                              value={
                                // @ts-ignore
                                field.value?.map((s) => ({
                                  label: getDocumentLabel(s),
                                  value: s,
                                })) ?? ([] as const)
                              }
                              options={allDocuments?.map((d) => ({
                                value: d.id,
                                label: getDocumentLabel(d.id),
                              }))}
                            />
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <div style={{ marginBottom: "6px" }} />
                      <CustomSelect
                        control={methods.control}
                        fullWidth
                        selectVariant="standard"
                        data={enumToValueOptions(ProposalLayout)}
                        {...methods.register("layout")}
                        label="Proposal Format"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mt={1} mb={4}>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="h6">Attached Sections</MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={8}>
                      <Controller
                        name="contentSettingIds"
                        control={methods.control}
                        render={({ field }) => (
                          <CustomMultipleSelect
                            {...field}
                            fullWidth
                            label="Proposal Sections"
                            value={
                              field.value?.map((id) => {
                                const option = proposalSectionOptions.find(
                                  (item) => item.value === id
                                );
                                return option ?? { label: id, value: id };
                              }) ?? []
                            }
                            options={proposalSectionOptions}
                            onChange={(options) =>
                              handleProposalSectionsChange(
                                (options ?? []).map((option) => option.value as string)
                              )
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <MDTypography variant="h5" mt={3}>
                    Internal Proposal Notes
                  </MDTypography>
                  <MDBox mt={2}>
                    <TextareaAutosize
                      {...methods.register("proposalNotes")}
                      minRows={10}
                      style={{ minWidth: "100%" }}
                    />
                  </MDBox>
                  <MDBox>
                    <ActionConfirmDialog
                      open={open}
                      setOpen={setOpen}
                      handleAccept={onAccept}
                      handleCancel={onCancel}
                      title="Confirmation"
                      content={
                        error?.replace("Please input a job size.", "Proceed?") ||
                        "Are you sure you want to proceed?"
                      }
                    />
                  </MDBox>
                  <Accordion>
                    <AccordionSummary
                      sx={{
                        backgroundColor: "rgba(100, 100, 150, 0.2)",
                        marginTop: "30px",
                      }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <MDTypography>Overwrite Proposal Acceptance</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3} mt={1}>
                        <Grid item xs={12}>
                          {proposalContent && (
                            <Controller
                              control={methods.control}
                              name="acceptanceOverride"
                              render={({ field, fieldState: { error } }) => (
                                <>
                                  <WYSIWYG
                                    type="proposal"
                                    content={
                                      proposal.acceptanceOverride ??
                                      proposalContent.find((type) => type.type === "ACCEPTANCE")
                                        ?.content
                                    }
                                    onChange={(content) => field.onChange(content)}
                                    title="Overwrite Proposal Acceptance"
                                    tooltip="Overwrite Proposal Acceptance"
                                  />
                                </>
                              )}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </FormProvider>
              </MDBox>
            </MDBox>
          </AccordionDetails>
        </Accordion>
        <MDBox my={3} display="flex" justifyContent="space-between">
          <Tooltip
            title={
              !hasValidStages
                ? stageValidationMessage
                : proposalIsDirty
                ? "Please save your changes and review for accuracy to preview the proposal"
                : ""
            }
            placement="top"
          >
            <span>
              <MDButton
                color="blue"
                onClick={(e) => {
                  e.preventDefault();
                  if (!proposalIsDirty && hasValidStages) {
                    openPreviewConfirmModal();
                  }
                }}
                disabled={proposalIsDirty || !hasValidStages}
              >
                Review Proposal
              </MDButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              !hasValidStages
                ? stageValidationMessage
                : proposalIsDirty
                ? "Please save your changes and review for accuracy to send the proposal"
                : ""
            }
            placement="top"
          >
            <span>
              <MDButton
                color="success"
                variant="gradient"
                type="submit"
                disabled={proposalIsDirty || !hasValidStages}
              >
                Send Proposal
              </MDButton>
            </span>
          </Tooltip>
        </MDBox>
      </MDBox>

      <Modal open={openModal} onClose={onClose} styles={{ maxWidth: "auto" }}>
        <ProposalStageSummary onCloseButton={onClose} />
      </Modal>

      <ProposalConfirmationModal
        key={proposal.stages?.[0].name}
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        proposal={proposal}
        emailBody={data?.proposalSentEmailText || ""}
        onSendProposal={async (emailData) => {
          // setting values here in case job size is null and modal is opened
          methods.setValue("emailSubjectOverride", emailData.subject);
          methods.setValue("emailBodyOverride", emailData.body);
          methods.setValue("clientEmailOverride", emailData.emails?.[0]);
          setEmails(emailData.emails);
          const sendValues = toProposalMutationVariables(
            methods.getValues() as SendProposalFormValues,
            proposal.contentSettings
          ) as SendProposalMutationVariables;
          await handleSendProposal({
            ...sendValues,
            emails: emailData.emails,
            emailSubjectOverride: emailData.subject,
            emailBodyOverride: emailData.body,
          });
        }}
        onPreviewProposal={async (emailData) => {
          // setting values here in case job size is null and modal is opened
          methods.setValue("emailSubjectOverride", emailData.subject);
          methods.setValue("emailBodyOverride", emailData.body);
          methods.setValue("clientEmailOverride", emailData.emails?.[0]);

          await unifiedPreviewHandler(emailData);
        }}
        loading={methods.formState.isSubmitting}
      />
    </>
  );
}

export const Recipients = () => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `emails`,
  });

  return (
    <>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={6} lg={4}>
          <MDTypography variant="h6">Main Contact Recipient</MDTypography>
        </Grid>
        <Grid item xs={12} md={6} lg={8}>
          <FormField {...register(`emails.0`)} error={errors["emails"]?.[0]} />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox display="flex" width="100%" alignItems="center">
            <MDTypography variant="h6">Additional Recipients</MDTypography>
            <IconButton onClick={() => append("")}>
              <Icon>add_circle</Icon>
            </IconButton>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={8}>
          {fields
            .slice(1, Infinity)
            .reverse()
            .map((field, i) => (
              <MDBox key={i} display="flex" width="100%" alignItems="center">
                <FormField
                  label="Additional Recipient"
                  {...register(`emails.${i + 1}`)}
                  error={errors["emails"]?.[i + 1]}
                />
                <IconButton onClick={() => remove(i + 1)}>
                  <Icon color="error">delete</Icon>
                </IconButton>
              </MDBox>
            ))}
        </Grid>
      </Grid>
    </>
  );
};
